import { z } from 'zod';
import {
  decodeHistoryDuration,
  patientHistoryCategories,
  patientHistoryDateReferences,
  patientHistoryDateUnits,
  patientHistoryKinds,
  patientHistoryPeriodicities,
  patientHistorySystems,
  type PatientHistoryCategory,
  type PatientHistoryDateReference,
  type PatientHistoryDateUnit,
  type PatientHistoryKind,
  type PatientHistoryPeriodicity,
  type PatientHistorySystem,
} from '../enums.js';
import { dateOnlySchema, optionalText, trimmed } from './_fields.js';

const historyObject = z
  .object({
    kind: z.enum(patientHistoryKinds),
    category: z.enum(patientHistoryCategories).nullable().optional(),
    systemType: z.enum(patientHistorySystems).nullable().optional(),
    title: trimmed(200),
    description: optionalText(4000).optional(),
    date: dateOnlySchema.nullable().optional(),
    /**
     * « Il y a », « à l'âge de » ou, pour un antécédent, « en » + année. Facultatif : une
     * sphère ancienne n'en a pas, et l'écran affiche alors « il y a » sans réécrire la ligne.
     * À la création d'une sphère, le service pose `ago` quand le client omet le choix.
     */
    dateReference: z.enum(patientHistoryDateReferences).nullable().optional(),
    /** Le 15 de « il y a 15 mois », avec `dateUnit` ; ou l'année de « en 2015 », sans unité. */
    dateAmount: z.int().min(0).max(50_000).nullable().optional(),
    dateUnit: z.enum(patientHistoryDateUnits).nullable().optional(),
    periodicity: z.enum(patientHistoryPeriodicities).nullable().optional(),
    periodicityDuration: optionalText(60).optional(),
    isImportant: z.boolean().optional(),
    isActive: z.boolean().optional(),
    activeDuration: optionalText(60).optional(),
  })
  .strict();

/** Première année acceptée pour « en … » quand la naissance n'est pas connue. */
export const HISTORY_YEAR_MIN = 1900;

export type HistoryYearIssue = 'year_sphere' | 'year_required' | 'year_range' | 'year_unit';

export interface HistoryOnsetValue {
  kind?: PatientHistoryKind;
  dateReference?: PatientHistoryDateReference | null;
  dateAmount?: number | null;
  dateUnit?: PatientHistoryDateUnit | null;
}

/**
 * « En » + année : antécédent seulement, l'année est exigée, sans unité, entre `minYear`
 * (1900, ou l'année de naissance quand le service la connaît) et l'année en cours.
 * `amountKnown` à faux : un correctif sans nombre garde l'année enregistrée.
 */
export function historyYearIssue(
  value: HistoryOnsetValue,
  { now = new Date(), minYear = HISTORY_YEAR_MIN, amountKnown = true } = {},
): HistoryYearIssue | null {
  if (value.dateReference !== 'year') return null;
  if (value.kind === 'sphere') return 'year_sphere';
  if (value.dateUnit != null) return 'year_unit';
  const year = value.dateAmount;
  if (year == null) return amountKnown ? 'year_required' : null;
  return year < minYear || year > now.getUTCFullYear() ? 'year_range' : null;
}

export const HISTORY_YEAR_ERRORS: Record<HistoryYearIssue, string> = {
  year_sphere: 'Une sphère n’a pas de repère « en » + année',
  year_required: 'Indiquez l’année',
  year_range: 'Année hors bornes',
  year_unit: 'Une année n’a pas d’unité',
};

function reportHistoryYear(
  value: HistoryOnsetValue,
  ctx: z.RefinementCtx,
  amountKnown: boolean,
): void {
  const issue = historyYearIssue(value, { amountKnown });
  if (issue === null) return;
  ctx.addIssue({
    code: 'custom',
    message: HISTORY_YEAR_ERRORS[issue],
    path: [issue === 'year_sphere' ? 'dateReference' : 'dateAmount'],
  });
}

export type HistoryActivityIssue = 'ponctuel_activity' | 'ongoing_duration' | 'duration_required';

export interface HistoryActivity {
  periodicity?: PatientHistoryPeriodicity | null;
  isActive?: boolean;
  activeDuration?: string | null;
}

/**
 * Activité d'un antécédent ou d'une sphère, comme la V1 la demandait. Ponctuel : ni état
 * « en cours » ni durée. Périodique : en cours ou non ; en cours, rien de plus ; sinon,
 * « pendant » combien de temps (`6:months`). Une périodique sans état vaut « pas en cours ».
 * `isActive` porte « en cours » : l'ancien interrupteur « Actif » avait ce sens.
 */
export function historyActivityIssue(value: HistoryActivity): HistoryActivityIssue | null {
  if (value.periodicity !== 'periodique') {
    return value.isActive !== undefined || value.activeDuration != null
      ? 'ponctuel_activity'
      : null;
  }
  if (value.isActive === true) {
    return value.activeDuration != null ? 'ongoing_duration' : null;
  }
  const duration = decodeHistoryDuration(value.activeDuration ?? null);
  return duration === null || duration.amount < 1 ? 'duration_required' : null;
}

export const HISTORY_ACTIVITY_ERRORS: Record<HistoryActivityIssue, string> = {
  ponctuel_activity: 'Une entrée ponctuelle n’a ni état en cours ni durée',
  ongoing_duration: 'Une entrée en cours n’a pas de durée',
  duration_required: 'Indiquez pendant combien de temps',
};

function reportHistoryActivity(value: HistoryActivity, ctx: z.RefinementCtx): void {
  const issue = historyActivityIssue(value);
  if (issue === null) return;
  ctx.addIssue({
    code: 'custom',
    message: HISTORY_ACTIVITY_ERRORS[issue],
    path: [issue === 'ponctuel_activity' ? 'isActive' : 'activeDuration'],
  });
}

/** Une sphère sans système n'a pas de sens : la V1 le demandait en premier. */
export const historyCreateSchema = historyObject
  .refine((value) => value.kind !== 'sphere' || value.systemType != null, {
    error: 'Système requis pour une sphère',
    path: ['systemType'],
  })
  .superRefine((value, ctx) => {
    reportHistoryActivity(value, ctx);
    reportHistoryYear(value, ctx, true);
  });
export type HistoryCreateInput = z.infer<typeof historyCreateSchema>;

/**
 * Le correctif seul ne dit pas tout de l'entrée : on ne juge l'activité que si la périodicité
 * est dans le correctif. Le service tranche le reste avec la ligne enregistrée.
 */
export const historyUpdateSchema = historyObject
  .partial()
  .strict()
  .superRefine((value, ctx) => {
    reportHistoryYear(value, ctx, value.dateAmount !== undefined);
    if (value.periodicity !== undefined) {
      reportHistoryActivity(value, ctx);
      return;
    }
    if (value.isActive === true && value.activeDuration != null) {
      reportHistoryActivity({ ...value, periodicity: 'periodique' }, ctx);
    }
  });
export type HistoryUpdateInput = z.infer<typeof historyUpdateSchema>;

export interface HistoryEntryDto {
  id: string;
  patientId: string;
  kind: PatientHistoryKind;
  category: PatientHistoryCategory | null;
  systemType: PatientHistorySystem | null;
  title: string;
  description: string | null;
  date: string | null;
  dateReference: PatientHistoryDateReference | null;
  dateAmount: number | null;
  dateUnit: PatientHistoryDateUnit | null;
  periodicity: PatientHistoryPeriodicity | null;
  periodicityDuration: string | null;
  isImportant: boolean;
  isActive: boolean;
  activeDuration: string | null;
  createdAt: string;
  updatedAt: string;
}
